import React, { useState, useEffect, useRef, useMemo } from "react";

const MultiSelectSKU = ({
  options = [],
  selected = [],
  onChange,
  placeholder = "Select plants",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);

  // Normalize options to { value, label }
  const normalizedOptions = useMemo(() => {
    return options.map((option) => {
      if (typeof option === "object" && option !== null) {
        return {
          value: option.value || option.sku || "",
          label: option.label || option.sku_lot_name || String(option.value || ""),
        };
      }
      return { value: String(option), label: String(option) };
    });
  }, [options]);

  const selectedValues = useMemo(() => {
    return selected.map((item) => String(item.value));
  }, [selected]);

  // Filter options
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return normalizedOptions;
    const term = searchTerm.toLowerCase();
    return normalizedOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(term) ||
        String(option.value).toLowerCase().includes(term)
    );
  }, [normalizedOptions, searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Toggle selection
  const toggleOption = (value) => {
    if (!onChange) return;
    const valueStr = String(value);
    const newSelected = selectedValues.includes(valueStr)
      ? selectedValues.filter((v) => v !== valueStr)
      : [...selectedValues, valueStr];

    const newSelectedObjects = normalizedOptions.filter((option) =>
      newSelected.includes(String(option.value))
    );
    onChange(newSelectedObjects);
  };

  // Select/deselect all visible
  const toggleSelectAllVisible = () => {
    if (!onChange || filteredOptions.length === 0) return;

    const allVisibleSelected = filteredOptions.every((option) =>
      selectedValues.includes(String(option.value))
    );

    let newSelected;
    if (allVisibleSelected) {
      const visibleValues = filteredOptions.map((option) => String(option.value));
      newSelected = selectedValues.filter(
        (value) => !visibleValues.includes(value)
      );
    } else {
      const visibleValues = filteredOptions.map((option) => String(option.value));
      const currentValuesSet = new Set(selectedValues);
      visibleValues.forEach((value) => currentValuesSet.add(value));
      newSelected = Array.from(currentValuesSet);
    }

    const newSelectedObjects = normalizedOptions.filter((option) =>
      newSelected.includes(String(option.value))
    );
    onChange(newSelectedObjects);
  };

  // Button text logic
  const buttonText = useMemo(() => {
    if (selected.length === 0) return placeholder;
    const joinedNames = selected.map((s) => s.label).join(", ");
    return joinedNames.length <= 1000
      ? joinedNames
      : `${selected.length} selected`;
  }, [selected, placeholder]);

  return (
    <div ref={containerRef} className="multiselect-dropdown relative w-full">
      {/* Toggle button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left border rounded p-2 bg-white flex items-center justify-between"
      >
        <span className="text-sm text-gray-700">{buttonText}</span>
        <span className="text-sm text-gray-500">▾</span>
      </button>

      {isOpen && (
        <div className="absolute z-40 mt-1 w-full rounded border bg-white shadow-md max-h-72 overflow-auto">
          {/* Search */}
          <div className="p-2 border-b">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search plants..."
              className="w-full p-2 border rounded text-sm"
            />
          </div>

          {/* Select all */}
          <div className="p-2 border-b">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={
                  filteredOptions.length > 0 &&
                  filteredOptions.every((option) =>
                    selectedValues.includes(String(option.value))
                  )
                }
                onChange={toggleSelectAllVisible}
                className="mr-2"
              />
              <span className="text-sm">
                {filteredOptions.length > 0
                  ? `Select all visible (${filteredOptions.length})`
                  : "No options available"}
              </span>
            </label>
          </div>

          {/* Options */}
          <div className="max-h-40 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(String(option.value))}
                    onChange={() => toggleOption(option.value)}
                    className="mr-2"
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))
            ) : (
              <div className="p-3 text-sm text-gray-500">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectSKU;
